/**
 * Helpers para ajustes de conceptos PLAME / Interna / Ambas.
 */

export function parseAjusteTemporal(valor) {
  if (valor == null) return null;
  if (typeof valor === 'object') {
    return {
      monto: valor.monto,
      destino: valor.destino || 'plame',
      monto_plame: valor.monto_plame,
      monto_interna: valor.monto_interna,
    };
  }
  return { monto: valor, destino: null };
}

function montoDefinido(valor) {
  return valor !== undefined && valor !== null && valor !== '';
}

export function getMontoDesdeAjuste(valor, fallback) {
  const parsed = parseAjusteTemporal(valor);
  if (!parsed || !montoDefinido(parsed.monto)) {
    return fallback;
  }
  return parsed.monto;
}

export function getDestinoDesdeAjuste(valor, defaultDestino = 'plame') {
  const parsed = parseAjusteTemporal(valor);
  return parsed?.destino || defaultDestino;
}

/** Monto del ajuste solo para la pestaña / canal pedido. El otro canal no se mezcla. */
export function getMontoDesdeAjusteParaCanal(valor, canal, fallback) {
  const parsed = parseAjusteTemporal(valor);
  if (!parsed) return fallback;
  const c = String(canal || 'plame').toLowerCase();
  if (c === 'interna') {
    if (montoDefinido(parsed.monto_interna)) return parsed.monto_interna;
    if (parsed.destino === 'interna' || parsed.destino === 'ambas') {
      return montoDefinido(parsed.monto) ? parsed.monto : fallback;
    }
    return fallback;
  }
  if (montoDefinido(parsed.monto_plame)) return parsed.monto_plame;
  if (parsed.destino === 'interna') return fallback;
  return montoDefinido(parsed.monto) ? parsed.monto : fallback;
}

export function ajusteTemporalTieneCanal(valor, canal) {
  const parsed = parseAjusteTemporal(valor);
  if (!parsed) return false;
  const c = String(canal || 'plame').toLowerCase();
  if (c === 'interna') {
    return montoDefinido(parsed.monto_interna)
      || parsed.destino === 'interna'
      || parsed.destino === 'ambas';
  }
  return montoDefinido(parsed.monto_plame)
    || parsed.destino === 'plame'
    || parsed.destino === 'ambas'
    || (parsed.destino == null && montoDefinido(parsed.monto));
}

/** Guarda PLAME e Interna por separado en el mismo concepto. */
export function mergeAjusteTemporalPorCanal(prevValor, nuevoMonto, destino) {
  const prev = parseAjusteTemporal(prevValor) || {};
  const dest = String(destino || prev.destino || 'plame').toLowerCase();
  const next = {
    monto: nuevoMonto,
    destino: dest,
    monto_plame: prev.monto_plame,
    monto_interna: prev.monto_interna,
  };
  if (dest === 'interna') {
    next.monto_interna = nuevoMonto;
  } else if (dest === 'ambas') {
    next.monto_plame = nuevoMonto;
    next.monto_interna = nuevoMonto;
  } else {
    next.monto_plame = nuevoMonto;
  }
  return next;
}

export function limpiarAjusteTemporalCanal(prevValor, canal) {
  const prev = parseAjusteTemporal(prevValor);
  if (!prev) return null;
  const c = String(canal || 'plame').toLowerCase();
  const next = { ...prev };
  if (c === 'interna') {
    delete next.monto_interna;
    if (next.destino === 'interna' || next.destino === 'ambas') {
      next.destino = montoDefinido(next.monto_plame) ? 'plame' : null;
    }
  } else {
    delete next.monto_plame;
    if (next.destino === 'plame' || next.destino === 'ambas' || next.destino == null) {
      next.destino = montoDefinido(next.monto_interna) ? 'interna' : null;
    }
  }
  if (!montoDefinido(next.monto_plame) && !montoDefinido(next.monto_interna) && !next.destino) {
    return null;
  }
  if (!montoDefinido(next.monto_plame) && !montoDefinido(next.monto_interna)) {
    return null;
  }
  next.monto = c === 'interna' ? next.monto_plame : next.monto_interna;
  return next;
}

export function destinoAplicaPlame(destino) {
  const d = String(destino || 'plame').toLowerCase();
  return d === 'plame' || d === 'ambas';
}

export function destinoAplicaInterna(destino) {
  const d = String(destino || 'plame').toLowerCase();
  return d === 'interna' || d === 'ambas';
}

export function empleadoTieneCanalInterno(empleado) {
  const tipo = String(
    empleado?.tipo_planilla
    || empleado?.tipoPlanilla
    || empleado?.datosEmpleado?.tipo_planilla
    || empleado?.datosSalariales?.tipoPlanilla
    || ''
  ).toLowerCase();
  return tipo === 'dual' || tipo === 'solo_interna';
}

/**
 * Destino "ambas" solo se copia a interna si el trabajador es Dual / solo interna.
 * En un trabajador solo PLAME no se duplica el monto PLAME dentro de la boleta interna.
 */
export function destinoAplicaInternaParaEmpleado(destino, empleado) {
  if (!destinoAplicaInterna(destino)) return false;
  const dest = String(destino || '').toLowerCase();
  if (dest === 'ambas' && empleado && !empleadoTieneCanalInterno(empleado)) {
    return false;
  }
  return true;
}

function normalizarTipoBoletaTareo(tipoBoleta) {
  return String(tipoBoleta || '').toLowerCase().trim();
}

function esTipoBoletaInterna(tb) {
  return tb === 'interna' || tb === 'interno';
}

function esTipoBoletaPlame(tb) {
  return tb === 'plame' || tb === '';
}

function esTipoBoletaAmbas(tb) {
  return tb === 'ambas' || tb === 'ambos' || tb === 'plame+interna';
}

/** Fila de tareo/ajuste que aplica a la boleta pedida. "ambas" cuenta una vez por canal. */
export function tipoBoletaAplicaACanal(tipoBoleta, canal) {
  const tb = normalizarTipoBoletaTareo(tipoBoleta);
  const c = String(canal || 'plame').toLowerCase();
  if (esTipoBoletaAmbas(tb)) return true;
  if (c === 'interna') return esTipoBoletaInterna(tb);
  return esTipoBoletaPlame(tb);
}

/**
 * Si el mismo día/código tiene fila interna, el cálculo interno ignora PLAME y ambas.
 * Una sola fila "ambas" (sin hermana) sí cuenta una vez en el canal pedido.
 * Trabajador solo_interna: PLAME no toma esa fila.
 */
export function conceptoTareoAplicaACanal(concepto, canal, conceptosDelTareo = [], empleado = null) {
  if (!concepto) return false;
  const c = String(canal || 'plame').toLowerCase();
  const tb = normalizarTipoBoletaTareo(concepto.tipo_boleta);
  const tipoEmpleado = String(
    empleado?.tipo_planilla
    || empleado?.tipoPlanilla
    || empleado?.datosEmpleado?.tipo_planilla
    || empleado?.datosSalariales?.tipoPlanilla
    || ''
  ).toLowerCase();

  if (c === 'plame' && tipoEmpleado === 'solo_interna') return false;

  const codigo = codigoConceptoAjuste(concepto);
  const lista = Array.isArray(conceptosDelTareo) && conceptosDelTareo.length
    ? conceptosDelTareo
    : [concepto];
  const hermanos = lista.filter((h) => {
    if (!codigo) return h === concepto;
    return codigoConceptoAjuste(h) === codigo;
  });
  const tipos = hermanos.map((h) => normalizarTipoBoletaTareo(h?.tipo_boleta));
  const hayInterna = tipos.some((t) => esTipoBoletaInterna(t));
  const hayPlame = tipos.some((t) => esTipoBoletaPlame(t));

  if (c === 'interna') {
    if (esTipoBoletaInterna(tb)) return true;
    if (esTipoBoletaPlame(tb)) return false;
    if (esTipoBoletaAmbas(tb)) return !hayInterna;
    return false;
  }

  if (esTipoBoletaInterna(tb)) return false;
  if (esTipoBoletaPlame(tb)) return true;
  if (esTipoBoletaAmbas(tb)) return !hayPlame;
  return false;
}

function esViaticoNoFusionable(concepto) {
  const codigo = codigoConceptoAjuste(concepto);
  if (codigo === '0917') return true;
  const origen = String(concepto?.origenCalculo || concepto?.origen_calculo || concepto?.origen || '').toLowerCase();
  return origen.includes('viatico');
}

/**
 * Un concepto con destino solo Interna no debe listarse en boleta PLAME.
 * Excepción: 0121 (sueldo básico dual) conserva el monto PLAME original.
 */
export function conceptoVaABoletaPlame(concepto) {
  const dest = String(concepto?.destino_ajuste || concepto?.destino || 'plame').toLowerCase();
  if (dest !== 'interna') return true;
  return codigoConceptoAjuste(concepto) === '0121';
}

export function montoInternaDesdeAjuste(concepto) {
  const dest = String(concepto?.destino_ajuste || 'plame').toLowerCase();
  if (dest === 'interna') {
    if (concepto?.monto_interna == null || concepto.monto_interna === '') return null;
    const n = Number(concepto.monto_interna);
    return Number.isFinite(n) ? n : null;
  }
  if (dest === 'ambas') {
    if (concepto?.monto_interna != null && concepto.monto_interna !== '') {
      const n = Number(concepto.monto_interna);
      return Number.isFinite(n) ? n : Number(concepto?.monto) || 0;
    }
    return Number(concepto?.monto) || 0;
  }
  return null;
}

/**
 * Pasa consideraciones destino Interna/Ambas a ingresosInternos / descuentosInternos.
 * El 0121 interna actualiza sueldo interno; no se duplica como extra.
 */
export function fusionarAjustesEnInterna({
  ingresos = [],
  descuentos = [],
  ingresosInternos = [],
  descuentosInternos = [],
  empleado = null,
} = {}) {
  const cloneIng = (ingresosInternos || []).map((c) => ({ ...c }));
  const cloneDes = (descuentosInternos || []).map((c) => ({ ...c }));
  let sueldoInternoOverride = null;

  const upsert = (concepto, bucket, esIngreso) => {
    if (esViaticoNoFusionable(concepto)) return;
    if (!destinoAplicaInternaParaEmpleado(concepto?.destino_ajuste, empleado)) return;
    const monto = montoInternaDesdeAjuste(concepto);
    if (monto == null || !Number.isFinite(monto) || monto < 0) return;
    const codigo = codigoConceptoAjuste(concepto);
    if (!codigo) return;
    if (esIngreso && codigo === '0121') {
      sueldoInternoOverride = monto;
      return;
    }
    if (monto <= 0) return;
    const existente = bucket.find((x) => codigoConceptoAjuste(x) === codigo);
    if (existente) {
      existente.monto = monto;
      existente.origenCalculo = existente.origenCalculo || 'ajuste_sueldo_interna';
      return;
    }
    bucket.push({
      id: concepto.plame_configuracion_id || concepto.id,
      codigo,
      codigo_plame: codigo,
      codigoPlame: codigo,
      monto,
      tipo_concepto: esIngreso ? 'ingreso' : 'descuento',
      descripcion: concepto.descripcion || concepto.concepto || codigo,
      origenCalculo: 'ajuste_sueldo_interna',
      destino_ajuste: String(concepto.destino_ajuste).toLowerCase(),
    });
  };

  (ingresos || []).forEach((c) => upsert(c, cloneIng, true));
  (descuentos || []).forEach((c) => upsert(c, cloneDes, false));

  return {
    ingresosInternos: cloneIng,
    descuentosInternos: cloneDes,
    sueldoInternoOverride,
  };
}

export function codigoConceptoAjuste(concepto) {
  return String(
    concepto?.codigo
      || concepto?.codigoPlame
      || concepto?.codigo_plame
      || concepto?.plame_configuracion?.codigo_plame
      || ''
  );
}

/**
 * Resuelve montos a persistir según destino.
 * - plame: monto PLAME = nuevo; monto_interna sin cambio (null si no había)
 * - interna: monto PLAME = original; monto_interna = nuevo
 * - ambas: ambos = nuevo
 */
export function resolverMontosPorDestino({
  montoOriginal,
  montoNuevo,
  destino,
  montoInternaPrevio = null,
}) {
  const dest = String(destino || 'plame').toLowerCase();
  const original = Number(montoOriginal) || 0;
  const nuevo = montoNuevo !== undefined && montoNuevo !== null && montoNuevo !== ''
    ? Number(montoNuevo)
    : original;

  if (dest === 'interna') {
    return {
      monto: original,
      monto_interna: nuevo,
      destino_ajuste: 'interna',
    };
  }
  if (dest === 'ambas') {
    return {
      monto: nuevo,
      monto_interna: nuevo,
      destino_ajuste: 'ambas',
    };
  }
  return {
    monto: nuevo,
    monto_interna: montoInternaPrevio != null ? Number(montoInternaPrevio) : null,
    destino_ajuste: 'plame',
  };
}

export default {
  parseAjusteTemporal,
  getMontoDesdeAjuste,
  getMontoDesdeAjusteParaCanal,
  ajusteTemporalTieneCanal,
  mergeAjusteTemporalPorCanal,
  limpiarAjusteTemporalCanal,
  getDestinoDesdeAjuste,
  destinoAplicaPlame,
  destinoAplicaInterna,
  empleadoTieneCanalInterno,
  destinoAplicaInternaParaEmpleado,
  tipoBoletaAplicaACanal,
  conceptoTareoAplicaACanal,
  conceptoVaABoletaPlame,
  montoInternaDesdeAjuste,
  fusionarAjustesEnInterna,
  codigoConceptoAjuste,
  resolverMontosPorDestino,
};
